/**
 * AdjustPathModal — rewrite remaining Path Instance spine (canon 29 §2).
 * Gated by path_adjust_policy + catchball.adjust_path permission in the parent.
 */

import { useMemo, useState } from 'react';
import { MapPin, X, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';

export interface AdjustPathTarget {
  id: string;
  name: string;
}

interface AdjustPathModalProps {
  /** Current remaining spine org unit ids. */
  remainingSpine: string[];
  /** Planned Path (read-only reference). */
  plannedSpine?: string[];
  orgUnits: AdjustPathTarget[];
  onSubmit: (remainingOrgUnitIds: string[]) => Promise<void>;
  onClose: () => void;
}

export function AdjustPathModal({
  remainingSpine,
  plannedSpine = [],
  orgUnits,
  onSubmit,
  onClose,
}: AdjustPathModalProps) {
  const nameById = useMemo(() => {
    const m = new Map(orgUnits.map((u) => [u.id, u.name]));
    return m;
  }, [orgUnits]);

  const [order, setOrder] = useState<string[]>(
    remainingSpine.length > 0 ? [...remainingSpine] : [...plannedSpine],
  );
  const [addId, setAddId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const available = orgUnits.filter((u) => !order.includes(u.id));

  const move = (index: number, dir: -1 | 1) => {
    const next = [...order];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setOrder(next);
  };

  const remove = (index: number) => {
    setOrder((prev) => prev.filter((_, i) => i !== index));
  };

  const add = () => {
    if (!addId || order.includes(addId)) return;
    setOrder((prev) => [...prev, addId]);
    setAddId('');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to adjust path');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] dark:border-[var(--rule)] w-full max-w-lg mx-4 p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[var(--text-ter)]" />
              Adjust remaining path
            </h3>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
              Rewrite where this Loop goes from here. Stops already visited stay in
              the record; you only change the remaining spine.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {plannedSpine.length > 0 && (
          <p className="text-xs text-[var(--text-ter)]">
            Best-known path at creation:{' '}
            <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              {plannedSpine.map((id) => nameById.get(id) ?? id).join(' → ')}
            </span>
          </p>
        )}

        <div className="space-y-1">
          <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Remaining stops (top → bottom)
          </label>
          {order.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)] italic py-3">No remaining stops — add at least one.</p>
          ) : (
            <ul className="border border-[var(--rule)] dark:border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              {order.map((id, index) => (
                <li key={`${id}-${index}`} className="flex items-center gap-2 px-3 py-2">
                  <span className="text-xs text-[var(--text-ter)] w-5">{index + 1}</span>
                  <span className="flex-1 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                    {nameById.get(id) ?? id}
                  </span>
                  <button type="button" onClick={() => move(index, -1)} disabled={index === 0} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:opacity-30" title="Move up">
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => move(index, 1)} disabled={index === order.length - 1} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:opacity-30" title="Move down">
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => remove(index)} className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)]" title="Remove">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {available.length > 0 && (
          <div className="flex gap-2">
            <select
              value={addId}
              onChange={(e) => setAddId(e.target.value)}
              className="flex-1 text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)]"
            >
              <option value="">Add a stop…</option>
              {available.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={add}
              disabled={!addId}
              className="px-3 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] disabled:opacity-40"
            >
              Add
            </button>
          </div>
        )}

        {error && (
          <p className="text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">{error}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting || order.length === 0}
            className="px-4 py-2 text-sm font-medium bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save path'}
          </button>
        </div>
      </div>
    </div>
  );
}
